/**
 * Tab session restore for the chat experience. Kept in one module so Intro + App share the key.
 */
export const CHAT_SESSION_STORAGE_KEY = 'aotf_chat_restore_v1';

/** One-shot: intro artefact click (not browser back). App reads on artefact preferences URL (`/:key`) to clear in-memory chat state. */
const FRESH_ARTEFACT_FROM_INTRO_KEY = 'aotf_fresh_artefact_from_intro_v1';

export function markFreshArtefactFromIntro() {
  try {
    sessionStorage.setItem(FRESH_ARTEFACT_FROM_INTRO_KEY, '1');
  } catch (_) {
    /* ignore */
  }
}

export function consumeFreshArtefactFromIntro() {
  try {
    if (sessionStorage.getItem(FRESH_ARTEFACT_FROM_INTRO_KEY) !== '1') return false;
    sessionStorage.removeItem(FRESH_ARTEFACT_FROM_INTRO_KEY);
    return true;
  } catch (_) {
    return false;
  }
}

/** True when pathname is the home chooser (snapshot must not hydrate a chat session). */
export function isLandingPathname(pathname) {
  const p = typeof pathname === 'string' ? pathname : '';
  return p === '' || p === '/';
}

/**
 * Session id from tab storage for reload resume. Skips landing `/` so stale snapshots are not revived there.
 */
export function readPersistedSessionIdForPath(pathname) {
  if (typeof window === 'undefined' || isLandingPathname(pathname)) return null;
  return readChatSessionSnapshot()?.sessionId ?? null;
}

/** Parsed tab snapshot for resume logic (IntroPage, etc.). */
export function readChatSessionSnapshot() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(CHAT_SESSION_STORAGE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (data?.version !== 1 || !data.sessionId) return null;
    return data;
  } catch {
    return null;
  }
}

export function prefsGateStorageKey(artefactKey) {
  return `aotf_prefs_gate_${encodeURIComponent(artefactKey)}`;
}

export function clearPersistedChatSession() {
  try {
    sessionStorage.removeItem(CHAT_SESSION_STORAGE_KEY);
  } catch (_) {
    /* ignore */
  }
}

/**
 * When the user returns to the home (artefact) chooser and picks an artefact again, clear
 * prior tab state and per-artefact gates so the next visit is a full new run.
 */
export function clearArtefactFlowSessionStorage(artefactKey) {
  try {
    clearPersistedChatSession();
    sessionStorage.removeItem('disclaimer_acknowledged');
    sessionStorage.removeItem('introduction_completed');
    if (artefactKey) {
      sessionStorage.removeItem(prefsGateStorageKey(artefactKey));
    }
  } catch (_) {
    /* ignore */
  }
}

/**
 * Best-effort: end the server session that is still referenced in the tab snapshot.
 */
export async function endSessionFromPersistedSnapshotIfAny(apiService) {
  if (!apiService || typeof apiService.endSession !== 'function') return;
  try {
    const raw = sessionStorage.getItem(CHAT_SESSION_STORAGE_KEY);
    if (!raw) return;
    const data = JSON.parse(raw);
    const sid = data?.sessionId;
    if (sid && typeof sid === 'string') {
      await apiService.endSession(sid).catch(() => {});
    }
  } catch (_) {
    /* ignore */
  }
}

function removeAllPrefsGateKeys() {
  try {
    for (const k of Object.keys(sessionStorage)) {
      if (k.startsWith('aotf_prefs_gate_')) sessionStorage.removeItem(k);
    }
  } catch (_) {
    /* ignore */
  }
}

/** End server session if any, then wipe tab chat state (used when returning to the intro / home route). */
export async function resetClientSessionWhenReturningHome(apiService) {
  await endSessionFromPersistedSnapshotIfAny(apiService);
  try {
    clearPersistedChatSession();
    sessionStorage.removeItem('disclaimer_acknowledged');
    sessionStorage.removeItem('introduction_completed');
    removeAllPrefsGateKeys();
  } catch (_) {
    /* ignore */
  }
}
